// Hong Kong calendar dates for the booking lookup.
//
// Booking records carry a Hong Kong calendar date, and the backend applies
// `DATE(b.date) BETWEEN from_date AND to_date` in SQL *before* it computes eligibility — so a
// booking outside the queried range is invisible to the operator and its ticket cannot be created.
//
// Two ways of naming "today" are both wrong here and must stay out of this file:
//   * `new Date().toISOString().slice(0, 10)` is the UTC calendar date, which names the PREVIOUS
//     day for every Hong Kong operator between 00:00 and 07:59 HKT.
//   * local-time formatting (`moment().format('YYYY-MM-DD')`) just moves that bug onto any browser
//     whose clock is not set to Hong Kong time.
// The dates below are derived from the absolute instant instead, so they depend on neither.
//
// DOMAIN — instants from 1980 onward, which covers every instant a booking lookup can see. Hong
// Kong has observed UTC+8 with no daylight saving since 1979; sampled against the ICU timezone
// database every 3 hours from 1980 to 2035 it reports UTC+8 and nothing else, so the fixed offset
// is exact there and needs no timezone database in the browser. It is NOT exact before that — Hong
// Kong ran UTC+9 summer time in 1946, where this helper is a day out. Extreme instants are also
// out of domain: a `Date` within 8 hours of the ±8.64e15 ms limit overflows, and years outside
// 0000-9999 serialise as `±YYYYYY`, so neither yields a `YYYY-MM-DD`. Every caller passes
// `new Date()`.
const HONG_KONG_UTC_OFFSET_MINUTES = 8 * 60
const MS_PER_MINUTE = 60 * 1000
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE

// The Hong Kong calendar date `day_offset` days away from `now`, as `YYYY-MM-DD`.
// Shifting the instant by whole days is exact because the offset never changes.
export function hongKongDate(now = new Date(), day_offset = 0) {
  const shifted = now.getTime() + HONG_KONG_UTC_OFFSET_MINUTES * MS_PER_MINUTE + day_offset * MS_PER_DAY
  return new Date(shifted).toISOString().slice(0, 10)
}

// A booking's eligibility window is `[start - valid_before_minutes, start + valid_after_minutes)`
// evaluated in Hong Kong time; both columns are MINUTES (backend DB default 60). While neither
// bound exceeds 1440 minutes, every booking that can be eligible right now has a Hong Kong booking
// date within one day of today's, so the lookup spans today plus both neighbours. That is a claim
// about the backend configuration, not a definition — if a service type is ever given a grace
// period of a day or more, this constant is what has to grow with it.
export const BOOKING_LOOKUP_ADJACENT_DAYS = 1

// The `[from_date, to_date]` bounds for `GET /bookings`.
export function bookingLookupDateRange(now = new Date()) {
  return {
    from_date: hongKongDate(now, -BOOKING_LOOKUP_ADJACENT_DAYS),
    to_date: hongKongDate(now, BOOKING_LOOKUP_ADJACENT_DAYS)
  }
}
